/**
 * Visualiser renderer.
 *
 * Two backends, one picture: WebGPU when the browser exposes it, WebGL2
 * otherwise (and automatically when WebGPU setup fails, which is common —
 * blocklisted GPUs, missing adapters, failed pipeline validation). Both draw
 * the same full-screen shader, so a mode switch is a pipeline swap rather than
 * a re-render.
 */

import {
  FLOATS_PER_FRAME,
  GLSL_FRAGMENT,
  GLSL_VERTEX,
  wgslModule,
} from "./shaders.js";

export type VisualizerMode = "synthwave" | "rings" | "crt";

export const VISUALIZER_MODES: VisualizerMode[] = ["synthwave", "rings", "crt"];

export interface Frame {
  /** Seconds since start. */
  time: number;
  bass: number;
  mid: number;
  treble: number;
  level: number;
  /** 32 log-spaced bands in 0..1. */
  levels: Float32Array;
}

export type Backend = "webgpu" | "webgl2";

export interface Renderer {
  readonly backend: Backend;
  /** Swap the shader drawn on the next frame. */
  setMode(mode: VisualizerMode): void;
  render(frame: Frame): void;
  destroy(): void;
}

/** Create the best renderer this browser can give us, WebGL2 as the floor. */
export async function createRenderer(
  canvas: HTMLCanvasElement,
  mode: VisualizerMode,
): Promise<Renderer> {
  try {
    return await createWebGpuRenderer(canvas, mode);
  } catch (error) {
    console.warn("[visualizer] WebGPU unavailable, using WebGL2:", error);
  }
  return createGlRenderer(canvas, mode);
}

// ------------------------------------------------------------------ WebGL2

function createGlRenderer(canvas: HTMLCanvasElement, mode: VisualizerMode): Renderer {
  const gl = canvas.getContext("webgl2", {
    alpha: false,
    antialias: false,
    depth: false,
    stencil: false,
    powerPreference: "high-performance",
  });
  if (!gl) throw new Error("WebGL2 is not available");

  const programs = new Map<VisualizerMode, GlProgram>();
  for (const entry of VISUALIZER_MODES) {
    programs.set(entry, compileGlProgram(gl, entry));
  }
  let current = mode;

  return {
    backend: "webgl2",
    setMode(next: VisualizerMode): void {
      if (programs.has(next)) current = next;
    },
    render(frame: Frame): void {
      const program = programs.get(current);
      if (!program) return;
      const width = canvas.width;
      const height = canvas.height;
      gl.viewport(0, 0, width, height);
      gl.useProgram(program.handle);
      gl.uniform1f(program.u.uTime, frame.time);
      gl.uniform2f(program.u.uRes, width, height);
      gl.uniform1f(program.u.uBass, frame.bass);
      gl.uniform1f(program.u.uMid, frame.mid);
      gl.uniform1f(program.u.uTreble, frame.treble);
      gl.uniform1f(program.u.uLevel, frame.level);
      if (program.u.uLevels !== null) {
        gl.uniform4fv(program.u.uLevels, frame.levels.subarray(0, 32));
      }
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    },
    destroy(): void {
      for (const program of programs.values()) gl.deleteProgram(program.handle);
      programs.clear();
      const lose = gl.getExtension("WEBGL_lose_context");
      lose?.loseContext();
    },
  };
}

interface GlUniforms {
  uTime: WebGLUniformLocation | null;
  uRes: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uTreble: WebGLUniformLocation | null;
  uLevel: WebGLUniformLocation | null;
  uLevels: WebGLUniformLocation | null;
}

interface GlProgram {
  handle: WebGLProgram;
  u: GlUniforms;
}

function compileGlProgram(
  gl: WebGL2RenderingContext,
  mode: VisualizerMode,
): GlProgram {
  const fragmentSource = GLSL_FRAGMENT[mode];
  if (!fragmentSource) throw new Error(`unknown visualiser mode: ${mode}`);

  const vertex = compileShader(gl, gl.VERTEX_SHADER, GLSL_VERTEX, `${mode} vertex`);
  const fragment = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource, `${mode} fragment`);
  const handle = gl.createProgram();
  if (!handle) throw new Error("could not create WebGL program");
  gl.attachShader(handle, vertex);
  gl.attachShader(handle, fragment);
  gl.linkProgram(handle);
  gl.deleteShader(vertex);
  gl.deleteShader(fragment);
  if (!gl.getProgramParameter(handle, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(handle) ?? "unknown link error";
    gl.deleteProgram(handle);
    throw new Error(`link failed (${mode}): ${log}`);
  }

  return {
    handle,
    u: {
      uTime: gl.getUniformLocation(handle, "uTime"),
      uRes: gl.getUniformLocation(handle, "uRes"),
      uBass: gl.getUniformLocation(handle, "uBass"),
      uMid: gl.getUniformLocation(handle, "uMid"),
      uTreble: gl.getUniformLocation(handle, "uTreble"),
      uLevel: gl.getUniformLocation(handle, "uLevel"),
      uLevels: gl.getUniformLocation(handle, "uLevels[0]"),
    },
  };
}

function compileShader(
  gl: WebGL2RenderingContext,
  type: number,
  source: string,
  label: string,
): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error(`could not create shader: ${label}`);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) ?? "unknown compile error";
    gl.deleteShader(shader);
    throw new Error(`shader compile failed (${label}): ${log}`);
  }
  return shader;
}

// ------------------------------------------------------------------- WebGPU

/** Minimal structural types: the DOM lib does not ship WebGPU declarations. */
interface GpuLike {
  requestAdapter(options?: unknown): Promise<GpuAdapterLike | null>;
  getPreferredCanvasFormat(): string;
}

interface GpuAdapterLike {
  requestDevice(): Promise<GpuDeviceLike>;
}

interface GpuBufferLike {
  destroy?(): void;
}

interface GpuPipelineLike {
  getBindGroupLayout(index: number): unknown;
}

interface GpuDeviceLike {
  queue: {
    writeBuffer(buffer: GpuBufferLike, offset: number, data: BufferSource): void;
    submit(buffers: unknown[]): void;
  };
  createShaderModule(descriptor: unknown): unknown;
  createRenderPipeline(descriptor: unknown): GpuPipelineLike;
  createBuffer(descriptor: unknown): GpuBufferLike;
  createBindGroup(descriptor: unknown): unknown;
  pushErrorScope(filter: string): void;
  popErrorScope(): Promise<Error | null>;
  destroy?(): void;
}

interface GpuContextLike {
  configure(configuration: unknown): void;
  getCurrentTexture(): { createView(): unknown };
}

interface GpuCanvasLike {
  getContext(contextId: string): unknown;
}

interface GpuPassLike {
  setPipeline(pipeline: unknown): void;
  setBindGroup(index: number, group: unknown): void;
  draw(vertexCount: number): void;
  end(): void;
}

interface GpuEncoderLike {
  beginRenderPass(descriptor: unknown): GpuPassLike;
  /** Closes the encoder and returns the command buffer `submit` expects. */
  finish(): unknown;
}

interface GpuDeviceWithEncoder extends GpuDeviceLike {
  createCommandEncoder(): GpuEncoderLike;
}

function createEncoder(device: GpuDeviceLike): GpuEncoderLike {
  const withEncoder = device as GpuDeviceWithEncoder;
  if (typeof withEncoder.createCommandEncoder !== "function") {
    throw new Error("WebGPU device cannot create command encoders");
  }
  return withEncoder.createCommandEncoder();
}

function beginPass(encoder: GpuEncoderLike, attachment: unknown): GpuPassLike {
  return encoder.beginRenderPass({ colorAttachments: [attachment] });
}

async function createWebGpuRenderer(
  canvas: HTMLCanvasElement,
  mode: VisualizerMode,
): Promise<Renderer> {
  const gpu = (navigator as unknown as { gpu?: GpuLike }).gpu;
  if (!gpu) throw new Error("navigator.gpu is not available");

  const adapter = await gpu.requestAdapter({ powerPreference: "high-performance" });
  if (!adapter) throw new Error("no WebGPU adapter");
  const device = await adapter.requestDevice();
  const format = gpu.getPreferredCanvasFormat();

  // Shader/pipeline validation failures must not leave the canvas bound to a
  // half-configured WebGPU context, so prove the pipelines first.
  device.pushErrorScope("validation");
  const pipelines = new Map<VisualizerMode, GpuPipelineLike>();
  for (const entry of VISUALIZER_MODES) {
    const module = device.createShaderModule({ code: wgslModule(entry) });
    pipelines.set(
      entry,
      device.createRenderPipeline({
        layout: "auto",
        vertex: { module, entryPoint: "vs" },
        fragment: { module, entryPoint: "fs", targets: [{ format }] },
        primitive: { topology: "triangle-list" },
      }),
    );
  }
  const validationError = await device.popErrorScope();
  if (validationError) throw new Error(`WGSL validation failed: ${validationError.message}`);

  const context = (canvas as unknown as GpuCanvasLike).getContext("webgpu");
  if (!context || typeof context !== "object") throw new Error("no WebGPU canvas context");
  const gpuContext = context as GpuContextLike;
  gpuContext.configure({ device, format, alphaMode: "opaque" });

  const uniforms = device.createBuffer({
    size: FLOATS_PER_FRAME * 4,
    usage: 0x08 | 0x40, // UNIFORM | COPY_DST
  });
  const data = new Float32Array(FLOATS_PER_FRAME);
  const bindGroups = new Map<VisualizerMode, unknown>();
  for (const [entry, pipeline] of pipelines) {
    bindGroups.set(
      entry,
      device.createBindGroup({
        layout: pipeline.getBindGroupLayout(0),
        entries: [{ binding: 0, resource: { buffer: uniforms } }],
      }),
    );
  }
  let current = mode;

  return {
    backend: "webgpu",
    setMode(next: VisualizerMode): void {
      if (pipelines.has(next)) current = next;
    },
    render(frame: Frame): void {
      const pipeline = pipelines.get(current);
      const bindGroup = bindGroups.get(current);
      if (!pipeline || !bindGroup) return;

      data[0] = frame.time;
      data[1] = canvas.width;
      data[2] = canvas.height;
      data[3] = frame.bass;
      data[4] = frame.mid;
      data[5] = frame.treble;
      data[6] = frame.level;
      data[7] = 0;
      data.set(frame.levels.subarray(0, 32), 8);
      device.queue.writeBuffer(uniforms, 0, data);

      const commandEncoder = createEncoder(device);
      const pass = beginPass(commandEncoder, {
        view: gpuContext.getCurrentTexture().createView(),
        clearValue: { r: 0.01, g: 0.01, b: 0.03, a: 1 },
        loadOp: "clear",
        storeOp: "store",
      });
      pass.setPipeline(pipeline);
      pass.setBindGroup(0, bindGroup);
      pass.draw(3);
      pass.end();
      device.queue.submit([commandEncoder.finish()]);
    },
    destroy(): void {
      uniforms.destroy?.();
      device.destroy?.();
      pipelines.clear();
      bindGroups.clear();
    },
  };
}
